import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { selectAppStateSelectedCustomer } from '@app/ngrx-customer/store';
import { State } from '@app/store';
import { InventoryService } from '@inventory/services/inventory.service';
import {
  ActionSheetController,
  Platform,
  PopoverController,
} from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { perCase, perUnit } from '@shared/constants/inventory-constants';
import { FEATURES } from '@shared/constants/splitio-features';
import { Button } from '@shared/models/ellipsis-option.model';
import { DownloadPrintBaseService } from '@shared/services/document/download-print-base.service';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { GL_ACTIONS } from '@usf/ngrx-gl';
import {
  ExternalVendorState,
  NonUSFProductActions,
  ProductAnalyticsService,
  getExternalVendorById,
  selectNonUSFProductsState,
  selectPIMClassEntities,
} from '@usf/ngrx-product';
import {
  HierarchyDto,
  ImportErrorMessage,
  NonUSFProduct,
  NonUsfProductImportRequest,
  PIMClass,
  ProductContract,
  ProductInventory,
  ProductSummary,
} from '@usf/product-types';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  firstValueFrom,
} from 'rxjs';
import { filter, first, map, switchMap, take } from 'rxjs/operators';
import { AddNonUsfProductComponent } from '../components/modals/add-non-usf-product/add-non-usf-product.component';
import { ImportNonUsfProductModalComponent } from '../components/modals/import-non-usf-product-modal/import-non-usf-product-modal.component';
import {
  AddNonUsfViewModel,
  AlternateNonUsf,
  NonUSFProductViewModel,
  NonUsfRadioItem,
  PIMLevelEnum,
  manageNonUsfProductsViewModel,
  vendorDropdownEvent,
} from '../models/non-usf-products-view-model';
import { NonUsfProductViewModelSelectors } from '../selectors/non-usf-selectors';
import { Product } from '@shared/models/product.model';
import { NonUSFData } from '@shared/models/non-usf-data';
import { PriceViewModel } from '@shared/models/price-view.model';

@Injectable({
  providedIn: 'root',
})
export class ManageNonUsfProductsService {
  addNonUSFProductModalForm: FormGroup;
  addnonUsfProductModalSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  isEditModeForProductModal: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  deleteProductModalSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  importProductModalSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  importProductErrorsModalSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  importErrorListSubject: BehaviorSubject<ImportErrorMessage[]> =
    new BehaviorSubject([]);

  importNonUsfProductSuccessToastSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  importNonUsfProductSuccessToastModalSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  pimFeatureFlag$: Observable<boolean>;
  productForModal: NonUSFProductViewModel;
  addProductRootPage = AddNonUsfProductComponent;
  addProductParams: AddNonUsfViewModel;
  importProductParams: any;
  importSuccessModalParams: any;
  pimData$: Observable<PIMClass[]>;
  importModalRef = ImportNonUsfProductModalComponent;

  constructor(
    readonly http: HttpClient,
    readonly platformService: PlatformService,
    private actionSheetController: ActionSheetController,
    private popoverController: PopoverController,
    private translateService: TranslateService,
    private store: Store<State>,
    private nonUsfProductViewModelSelectors: NonUsfProductViewModelSelectors,
    private panAppState: PanAppState,
    public inventoryService: InventoryService,
    private loadingSpinnerService: LoadingSpinnerService,
    private analyticsService: ProductAnalyticsService,
    readonly platform: Platform,
    readonly downloadPrintBaseService: DownloadPrintBaseService,
  ) {
    this.pimFeatureFlag$ = this.panAppState.feature$([
      FEATURES.split_global_assign_pim_nonUsfProduct.name,
    ]);
    this.pimData$ = this.getPIMData$();
  }

  public prepareForCreateNonUSFProductModal = (
    vendors: ExternalVendorState[],
  ) => {
    this.productForModal = undefined;
    this.addNonUSFProductModalForm = new FormGroup({
      name: this.getNameForm(),
      brand: this.getBrandForm(),
      manufacturerNum: this.getManufactureForm(),
      packSize: this.getPackSizeForm(),
      avgNetWeight: this.getAvgNetWeightForm(),
      price: this.getPriceForm(),
      priceOption: this.getPriceOptionFrom(),
      units: this.getUnitsForm(),
      unitsPerCase: this.getUnitsPerCaseForm(),
      pimClass: this.getPIMClassForm(),
      pimCategory: this.getPIMCategoryForm(),
      pimGroup: this.getPIMGroupForm(),
    });
    this.setupVendorForm(undefined);
    this.setupModalViewModel(vendors);
  };

  public getSelections$ = () => {
    return this.store.select(
      this.nonUsfProductViewModelSelectors.getSelectionsArray(),
    );
  };

  public getPIMData$ = () => {
    return this.store.select(selectPIMClassEntities).pipe(
      map(pimState => {
        return Object.values(pimState);
      }),
    );
  };

  public preparePrePopulatedForm = (
    product: NonUSFProductViewModel,
    vendors: ExternalVendorState[],
  ) => {
    this.productForModal = product;
    this.addNonUSFProductModalForm = new FormGroup({
      name: this.getNameForm(product.productDescLong),
      brand: this.getBrandForm(product.brand),
      manufacturerNum: this.getManufactureForm(
        product.manufacturerProductNumber,
      ),
      packSize: this.getPackSizeForm(product.salesPackSize),
      avgNetWeight: this.getAvgNetWeightForm(product.averageNetWeight),
      price: this.getPriceForm(product.price),
      priceOption: this.getPriceOptionFrom(
        product.isPricePerUnitInd ? perUnit : perCase,
      ),
      units: this.getUnitsForm(product.priceUom),
      unitsPerCase: this.getUnitsPerCaseForm(product.unitsPerCase),
      pimClass: this.getPIMClassForm(product.pimClass),
      pimCategory: this.getPIMCategoryForm(product.category),
      pimGroup: this.getPIMGroupForm(product.groupId),
    });
    this.setupVendorForm(product.vendorId);
    this.setupModalViewModel(vendors);
  };

  getHierachyData = (pimData: PIMClass[]) => {
    let pimClassHeirachy: HierarchyDto = undefined;
    let pimCategoryHeirarchy: HierarchyDto = undefined;
    let pimGroupHeirarchy: HierarchyDto = undefined;

    const classCode = this.addNonUSFProductModalForm.get('pimClass').value;
    const pimClass = pimData.find(item => {
      return item.pimClass.code === classCode;
    });
    if (pimClass !== undefined) {
      pimClassHeirachy = pimClass.pimClass;
      const categoryCode =
        this.addNonUSFProductModalForm.get('pimCategory').value;
      const pimCategory = pimClass.pimCategories.find(item => {
        return item.pimCategory.code === categoryCode;
      });
      if (pimCategory !== undefined) {
        pimCategoryHeirarchy = pimCategory.pimCategory;
        const groupCode = this.addNonUSFProductModalForm.get('pimGroup').value;
        pimGroupHeirarchy = pimCategory.pimGroups.find(item => {
          return item.code === groupCode;
        });
      }
    }

    return [pimClassHeirachy, pimCategoryHeirarchy, pimGroupHeirarchy];
  };

  setupModalViewModel = async (vendors: ExternalVendorState[]) => {
    const [pimData, pimFlag] = await firstValueFrom(
      combineLatest([this.pimData$, this.pimFeatureFlag$]),
    );
    const [pimClassHeirachy, pimCategoryHeirarchy, pimGroupHeirarchy] =
      this.getHierachyData(pimData);
    this.addProductParams = {
      usfProductForm: this.addNonUSFProductModalForm,
      platform: this.platformService.platformType,
      vendors,
      pimData,
      originalProduct: this.productForModal,
      isAndroidTouch:
        this.platform.is('android') && this.platformService.isTouch.value
          ? true
          : false,
      pimClassHeirachy,
      pimCategoryHeirarchy,
      pimGroupHeirarchy,
      isEditMode: this.isEditModeForProductModal.value,
      dismisEvent: this.openOrCloseNonUsfProductModal,
      submitEvent: this.handleSubmitRequest,
      vendorsEvent: this.handleVendorsRequest,
      priceOptionEvent: this.handlePriceOptionRequest,
      unitsEvent: this.handleUnitRequest,
      pimCodeEvent: this.handlePimCodeSelection,
      pimFlag,
    };
  };

  getPriceForm(value: number | string = '') {
    return new FormControl(
      value,
      Validators.compose([
        Validators.maxLength(12),
        Validators.pattern(new RegExp(/^([0-9.,\$]+ ?)*$/)),
        this.numbersOnly(),
      ]),
    );
  }

  getUnitsPerCaseForm(value: number | string = '') {
    return new FormControl(
      value,
      Validators.compose([
        Validators.maxLength(3),
        Validators.pattern(new RegExp(/^[1-9]\d*$/)),
        this.numbersOnly(),
      ]),
    );
  }

  getAvgNetWeightForm(value: number | string = '') {
    return new FormControl(
      value,
      Validators.compose([
        Validators.maxLength(12),
        Validators.pattern(new RegExp(/^([0-9.]+ ?)*$/)),
        this.numbersOnly(),
      ]),
    );
  }

  getPackSizeForm(value: number | string = '') {
    return new FormControl(
      value,
      Validators.compose([Validators.maxLength(12)]),
    );
  }

  getManufactureForm(value: number | string = '') {
    return new FormControl(
      value,
      Validators.compose([Validators.maxLength(16)]),
    );
  }

  getBrandForm(value: string = '') {
    return new FormControl(
      value,
      Validators.compose([Validators.maxLength(25)]),
    );
  }

  getPIMClassForm(value: string = '') {
    return new FormControl(value, Validators.compose([]));
  }

  getPIMCategoryForm(value: string = '') {
    return new FormControl(value, Validators.compose([]));
  }

  getPIMGroupForm(value: string = '') {
    return new FormControl(value, Validators.compose([]));
  }

  getNameForm(value: string = '') {
    return new FormControl(
      value,
      Validators.compose([
        Validators.required,
        Validators.maxLength(75),
        Validators.minLength(1),
      ]),
    );
  }

  getPriceOptionFrom(value: string = perCase) {
    return new FormControl(value);
  }

  getUnitsForm(value: string = 'EA') {
    return new FormControl(value);
  }

  setupVendorForm(value: string) {
    if (value) {
      this.store
        .select(getExternalVendorById(value))
        .pipe(first())
        .subscribe(val => {
          this.addNonUSFProductModalForm.addControl(
            'vendor',
            new FormControl(val),
          );
        });
    } else {
      this.addNonUSFProductModalForm.addControl(
        'vendor',
        new FormControl(undefined),
      );
    }
  }

  private numbersOnly(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      if (control.value === undefined || control.value === null) {
        return null;
      }
      const numbersRegEx = new RegExp(
        /(^$)|(^[0-9]+)\.?([0-9]+)?$|^\.([0-9]+)?$/,
      );
      return !numbersRegEx.test(control.value)
        ? { numbersOnly: { value: control.value } }
        : null;
    };
  }

  isTouch = () => {
    return this.platformService.isTouch.value;
  };

  public openOrCloseNonUsfProductModal = (
    open: boolean,
    isEditMode: boolean = false,
  ) => {
    this.isEditModeForProductModal.next(isEditMode);
    this.addnonUsfProductModalSubject.next(open);
  };

  public openOrCloseNonUsfImportModal = (open: boolean) => {
    this.importProductModalSubject.next(open);
  };

  public openOrCloseNonUsfImportErrorsModal = (open: boolean) => {
    this.importProductErrorsModalSubject.next(open);
  };

  public downloadNonUsfImportErrors = () => {};

  public showImportNonUsfProductSuccessToast = (
    open: boolean,
    errors: ImportErrorMessage[] = [],
  ) => {
    if (open) {
    }
    this.importNonUsfProductSuccessToastSubject.next(open);
    this.importErrorListSubject.next(errors);
  };

  public openOrCloseImportNonUsfProductSuccessToastModal = (open: boolean) => {
    this.importNonUsfProductSuccessToastModalSubject.next(open);
  };

  public openOrCloseDeleteProductModal = (open: boolean) => {
    this.deleteProductModalSubject.next(open);
  };

  async openCreateNonUsfProductsOptions(
    viewModel: manageNonUsfProductsViewModel,
  ) {
    const buttons: any[] = [
      {
        text: this.translateService.instant(
          'i18n.inventory.createNonUsfProductsModal.addProductsManually',
        ),
        handler: () => {
          this.handleOpenRequestForUsfProductmodal(viewModel.vendors);
          this.dimsissModalController();
        },
      },
    ];

    const showImportNonUsfProductsOption =
      await this.panAppState.featureEnabled(
        FEATURES.split_global_import_non_usf_products.name,
      );

    if (showImportNonUsfProductsOption) {
      buttons.push({
        text: this.translateService.instant(
          'i18n.inventory.createNonUsfProductsModal.importAndUpdateProducts',
        ),
        handler: () => {
          this.handleOpenImportUsfProductModal(viewModel);
          this.dimsissModalController();
        },
      });
    }

    if (!this.platformService.isTouch.value) {
      const listOptionsPopover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        event,
        componentProps: {
          buttons,
        },
        cssClass: 'ellipsis-options-popover-desktop',
        mode: 'ios',
        showBackdrop: false,
      });
      await listOptionsPopover.present();
    } else {
      buttons.push({
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
      });
      const listOptionsActionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
      await listOptionsActionSheet.present();
    }
  }

  public handleOpenRequestForUsfProductmodal = (
    vendors: ExternalVendorState[],
  ) => {
    this.prepareForCreateNonUSFProductModal(vendors);
    this.openOrCloseNonUsfProductModal(true);
  };

  public handleOpenImportUsfProductModal = (
    viewModel: manageNonUsfProductsViewModel,
  ) => {
    this.importProductModalSubject.next(true);
    this.importProductParams = {
      platform: this.platformService.platformType,
      dismissEvent: this.openOrCloseNonUsfImportModal,
      submitEvent: this.handleImportSubmission,
      downloadTemplateEvent: this.downloadImportNonUsfProductTemplate,
      downloadGlCodeEvent: this.downloadGLCodeCsv,
      showDownloadGlCode: viewModel.showDownloadGlCode,
    };
  };

  public handleOpenImportUsfProductErrorsModal = () => {
    this.importProductErrorsModalSubject.next(true);
    this.openOrCloseNonUsfImportErrorsModal(true);
  };

  public handleOpenRequestForCardClick = (
    product: NonUSFProductViewModel,
    vendors: ExternalVendorState[],
  ) => {
    this.preparePrePopulatedForm(product, vendors);
    this.openOrCloseNonUsfProductModal(true, true);
  };

  public handleSubmitRequest = (isEditMode: boolean) => {
    this.openOrCloseNonUsfProductModal(false);
    if (isEditMode) {
      this.submitEdit();
    } else {
      this.submitCreation();
    }
  };

  getNonUsfProduct = async () => {
    const pimFlag = await firstValueFrom(this.pimFeatureFlag$);
    let price =
      Number.parseFloat(this.addNonUSFProductModalForm.get('price').value) ??
      null;
    if (price === 0) {
      price = null;
    }
    let vendor = this.addNonUSFProductModalForm.get('vendor').value;

    let nonUSFProduct: NonUSFProduct = {
      ...this.productForModal,
      productDescLong: this.addNonUSFProductModalForm.get('name').value,
      vendorId: vendor ? vendor.vendorId : undefined,
      brand: this.addNonUSFProductModalForm.get('brand').value,
      manufacturerProductNumber:
        this.addNonUSFProductModalForm.get('manufacturerNum').value,
      salesPackSize: this.addNonUSFProductModalForm.get('packSize').value,
      averageNetWeight:
        Number.parseFloat(
          this.addNonUSFProductModalForm.get('avgNetWeight').value,
        ) ?? undefined,
      price,
      priceUom: this.addNonUSFProductModalForm.get('units').value,
      isPricePerUnitInd:
        this.addNonUSFProductModalForm.get('priceOption').value === perUnit,
      unitsPerCase:
        Number.parseInt(
          this.addNonUSFProductModalForm.get('unitsPerCase').value,
        ) ?? undefined,
    };
    if (pimFlag) {
      nonUSFProduct = {
        ...nonUSFProduct,
        pimClass: this.addNonUSFProductModalForm.get('pimClass').value ?? '',
        category: this.addNonUSFProductModalForm.get('pimCategory').value ?? '',
        groupId: this.addNonUSFProductModalForm.get('pimGroup').value ?? '',
      };
    }

    return nonUSFProduct;
  };

  public submitEdit = async () => {
    const nonUSFProduct = await this.getNonUsfProduct();
    this.store.dispatch(NonUSFProductActions.update({ nonUSFProduct }));
  };

  public submitCreation = async () => {
    const selectedCustomer = await firstValueFrom(
      this.store.select(selectAppStateSelectedCustomer),
    );
    let nonUSFProduct: NonUSFProduct = await this.getNonUsfProduct();

    nonUSFProduct = {
      ...nonUSFProduct,
      divisionNumber: selectedCustomer.divisionNumber,
      customerNumber: selectedCustomer.customerNumber,
    };

    this.store.dispatch(NonUSFProductActions.create({ nonUSFProduct }));
  };
  public presentSelections = async (
    desktopClass: string,
    buttons: any[],
    event: Event,
  ) => {
    if (this.platformService.isTouch.value) {
      buttons.push({
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
      });
      const actionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
      await actionSheet.present();
    } else {
      const vendorPopover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        event: event,
        componentProps: {
          buttons,
        },
        showBackdrop: false,
        cssClass: desktopClass,
        mode: 'ios',
        side: 'bottom',
        alignment: 'center',
      });
      await vendorPopover.present();
    }
  };

  public handleVendorsRequest = async (event: vendorDropdownEvent) => {
    let buttons = [];
    event.vendors.forEach(vendor => {
      buttons.push({
        text: vendor.distributorName,
        handler: () => this.handleVendorSelection(vendor),
      });
    });

    if (buttons.length === 0) {
      buttons.push({
        text: this.translateService.instant(
          'i18n.nonUsfProducts.noVendorFound',
        ),
        handler: () => this.handleVendorSelection(undefined),
      });
    }

    await this.presentSelections(
      'vendor-selection-popover-desktop',
      buttons,
      event.event,
    );
  };

  handleUnitRequest = (event: Event) => {
    const buttons = [
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.uom.each',
        ),
        handler: () => this.handleUnitSelection('EA'),
      },
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.uom.pound',
        ),
        handler: () => this.handleUnitSelection('LB'),
      },
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.uom.ounce',
        ),
        handler: () => this.handleUnitSelection('OZ'),
      },
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.uom.grams',
        ),
        handler: () => this.handleUnitSelection('G'),
      },
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.uom.gallon',
        ),
        handler: () => this.handleUnitSelection('GA'),
      },
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.uom.batch',
        ),
        handler: () => this.handleUnitSelection('Batch'),
      },
      {
        text: this.translateService.instant(
          'i18n.inventory.inventoryWorksheet.uom.piece',
        ),
        handler: () => this.handleUnitSelection('Piece'),
      },
    ];

    this.presentSelections('vendor-selection-popover-desktop', buttons, event);
  };

  handlePriceOptionRequest = (event: Event) => {
    const buttons = [
      {
        text: perCase,
        handler: () => this.handlePriceOptionSelection(perCase),
      },
      {
        text: perUnit,
        handler: () => this.handlePriceOptionSelection(perUnit),
      },
    ];
    this.presentSelections('vendor-selection-popover-desktop', buttons, event);
  };

  handleVendorSelection = (vendor: ExternalVendorState) => {
    this.addNonUSFProductModalForm.get('vendor').setValue(vendor);
    this.dimsissModalController();
  };

  handleUnitSelection = (unit: string) => {
    this.addNonUSFProductModalForm.get('units').setValue(unit);
    this.dimsissModalController();
  };

  handlePriceOptionSelection = (option: string) => {
    this.addNonUSFProductModalForm.get('priceOption').setValue(option);
    this.dimsissModalController();
  };

  handlePimCodeSelection = (level: PIMLevelEnum, code: string) => {
    switch (level) {
      case PIMLevelEnum.class:
        this.addNonUSFProductModalForm.get('pimClass').setValue(code);
        break;
      case PIMLevelEnum.category:
        this.addNonUSFProductModalForm.get('pimCategory').setValue(code);
        break;
      case PIMLevelEnum.group:
        this.addNonUSFProductModalForm.get('pimGroup').setValue(code);
        break;
    }
  };

  dimsissModalController = () => {
    if (this.platformService.isTouch.value) {
      this.actionSheetController.dismiss();
    } else {
      this.popoverController.dismiss();
    }
  };

  handleCardSelection(nonUSFProduct: NonUSFProductViewModel) {
    this.store.dispatch(
      NonUSFProductActions.select({
        nonUSFProduct: nonUSFProduct,
        select: !nonUSFProduct.isSelected,
      }),
    );
  }

  resetSelections() {
    this.store.dispatch(NonUSFProductActions.resetSelections());
  }

  getTotalSelected$() {
    return this.nonUsfProductViewModelSelectors.manageNonUSFProductViewModel$.pipe(
      map(
        (vm: manageNonUsfProductsViewModel) =>
          vm.products.filter(product => product.isSelected).length,
      ),
    );
  }

  selectAnalyticsDataForNonUsfProducts = () => {
    return this.store.select(selectNonUSFProductsState).pipe(
      filter(nonUSFProductsState => nonUSFProductsState?.loaded),
      switchMap(() =>
        this.store.select(
          this.nonUsfProductViewModelSelectors.getNonUsfProductAnalyticsData(),
        ),
      ),
    );
  };

  trackManageNonUsfProducts = async () => {
    const productsForTracking = await firstValueFrom(
      this.selectAnalyticsDataForNonUsfProducts(),
    );
    this.analyticsService.trackManageNonUsfProductPageLoaded(
      productsForTracking,
    );
  };

  selectAll(nonUSFProducts: NonUSFProduct[]) {
    this.store.dispatch(NonUSFProductActions.selectAll({ nonUSFProducts }));
  }

  public async handleShowAddToInventory() {
    let buttons = [
      {
        text: this.translateService.instant(
          'i18n.inventory.nonUsfWorksheet.addToInventoryTitle',
        ),
        role: 'add to inventory',
        cssClass: 'add-to-inventory-button',
        handler: () => {
          const inventoryId = this.inventoryService.exposedLastInventory;
          this.inventoryService
            .selectInventoryById$(inventoryId)
            .pipe(take(1))
            .subscribe(async inventory => {
              this.getSelections$()
                .pipe(first())
                .subscribe(selections => {
                  this.inventoryService.openAddToInventoryModal(
                    selections,
                    this.platformService.isTouch.value,
                    this.platformService.platformType,
                    this.inventoryService.addNonUSFProdFromInventoryWorksheet
                      ? inventory
                      : undefined,
                    true,
                  );
                });
            });
        },
      },
      {
        text: this.translateService.instant('i18n.inventory.delete'),
        role: 'delete',
        cssClass: 'delete-button',
        handler: () => {
          this.openOrCloseDeleteProductModal(true);
        },
      },
    ];

    await this.presentSelections(
      'non-usf-product-selection-popover-desktop',
      buttons,
      event,
    );
  }

  searchNonUsfWorksheet(searchKey: string) {
    this.store.dispatch(
      NonUSFProductActions.searchNonUsfProductWorksheet({ keyword: searchKey }),
    );
  }

  deleteSelections = () => {
    this.getSelections$()
      .pipe(first())
      .subscribe(selections => {
        this.store
          .select(
            this.nonUsfProductViewModelSelectors.getNonUSfProductsBasedOnId(
              selections,
            ),
          )
          .pipe(first())
          .subscribe(nonUSFProducts => {
            this.store.dispatch(
              NonUSFProductActions.delete({ nonUSFProducts }),
            );
            this.resetSelections();
          });
      });
  };

  public downloadImportNonUsfProductTemplate = async () => {
    this.http
      .get(
        'assets/documents/example/Non_US_Foods_Products_Import_Template.csv',
        {
          responseType: 'text',
        },
      )
      .pipe(first())
      .subscribe(data =>
        this.downloadPrintBaseService.saveDocument(
          data,
          'Non_US_Foods_Products_Import_Template',
          'CSV',
          true,
        ),
      );
  };

  resetNonUsfSort() {
    this.store.dispatch(NonUSFProductActions.resetSort());
  }

  setNonUsfSort(headerName: string) {
    this.store.dispatch(NonUSFProductActions.setSort({ headerName }));
  }

  async openDownloadOverlay(viewModel: manageNonUsfProductsViewModel) {
    const buttons: Button[] = this.getDownloadButtons(viewModel);

    if (!this.platformService.isTouch.value) {
      const listOptionsPopover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        event,
        componentProps: {
          buttons,
        },
        cssClass: 'ellipsis-options-popover-desktop',
        mode: 'ios',
        showBackdrop: false,
      });
      await listOptionsPopover.present();
    } else {
      const listOptionsActionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons,
      });
      await listOptionsActionSheet.present();
    }
  }

  getDownloadButtons(viewModel: manageNonUsfProductsViewModel) {
    const buttons: Button[] = [];
    if (viewModel.totalNonUsfProducts > 0) {
      buttons.push({
        text: this.translateService.instant(
          'i18n.inventory.nonUsfDownload.downloadNonUsfProducts',
        ),
        handler: () => {
          this.downloadNonUsfProducts(viewModel.products);
          if (!this.platformService.isTouch.value) {
            this.popoverController.dismiss();
          }
        },
      });
    }
    if (viewModel.showDownloadGlCode) {
      buttons.push({
        text: this.translateService.instant(
          'i18n.inventory.nonUsfDownload.downloadGlCodes',
        ),
        handler: () => {
          this.downloadGLCodeCsv();
          if (!this.platformService.isTouch.value) {
            this.popoverController.dismiss();
          }
        },
      });
    }
    if (this.platformService.isTouch.value) {
      buttons.push({
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
      });
    }
    return buttons;
  }

  downloadNonUsfProducts(nonUsfProducts: NonUSFProduct[]) {
    const nonUsfProductNumbers: number[] = nonUsfProducts.map(
      product => product.productNumber,
    );

    this.loadingSpinnerService.createSpinnerModal().then(() => {
      this.store.dispatch(
        NonUSFProductActions.downloadNonUsfProductData({
          nonUsfProductNumbers,
        }),
      );
    });
  }

  downloadGLCodeCsv = () => {
    this.store.dispatch(GL_ACTIONS.downloadGlCodeCsv());
  };

  async downloadImportErrors(customerNumber) {
    const csvValues = this.convertErrorsToCsvString(
      this.importErrorListSubject.value,
    );
    await this.downloadPrintBaseService.saveDocument(
      csvValues,
      `ImportNonUSFoodsErrors_${customerNumber}`,
      'CSV',
      true,
    );
  }

  convertErrorsToCsvString(errors: ImportErrorMessage[]): string {
    let csValueString = 'Row Number, 	Error Description\n';
    errors.forEach(error => {
      csValueString += `${error.rowNumber},${this.escapeCsvValue(error.message)}\n`;
    });

    return csValueString;
  }

  escapeCsvValue(value: string): string {
    if (value.includes('"')) {
      value = value.replace(/"/g, '""');
    }
    if (value.includes(',')) {
      value = `"${value}"`;
    }
    return value;
  }

  handleImportSubmission = (nonUSFProducts: NonUsfProductImportRequest[]) => {
    this.store.dispatch(NonUSFProductActions.import({ nonUSFProducts }));
  };

  clearImportErrors() {
    this.importErrorListSubject.next([]);
    this.showImportNonUsfProductSuccessToast(false);
  }

  getNonUsfRadioItemsAsProducts$ = () => {
    return this.nonUsfProductViewModelSelectors.manageNonUSFProductViewModel$.pipe(
      map(viewModel => {
        const nonUsfRadioItems: Product[] = viewModel.products.map(product => {
          return {
            productNumber: product.productNumber,
            summary: {
              divisionNumber: product.divisionNumber,
              productNumber: product.productNumber,
              brand: product.brand,
              productDescTxtl: product.productDescLong,
              productDescLong: product.productDescLong,
              netWeight: product.averageNetWeight,
              salesPackSize: product.salesPackSize,
              priceUom: product.priceUom,
              nonUsfData: {
                manufacturerProductNumber: product.manufacturerProductNumber,
                vendorName: product.vendorName,
                selected: false,
              } as NonUSFData,
            } as unknown as ProductSummary,
            contract: {} as ProductContract,
            inventory: {} as ProductInventory,
            pricing: {
              unitPrice: product.price,
            } as PriceViewModel,
            isNonUsf: true,
          };
        });
        return nonUsfRadioItems;
      }),
    );
  };
}
